import React, { useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { AlertContent, ModalFactory, Tag } from 'components';
import { Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { ItemContainer, LoadingSkeleton, StyledIconButton } from 'screens/SiteSettings/styles';
import { TipCalculationsDescription } from 'screens/SiteSettings/TipCalculator/shared/styles';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Employees, selectEmployees } from 'store/Collections';
import { Employee, updateEmployeeSiteRoleRequest } from 'store/Employee';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { IEmployee, IEmployeeSiteWithholdings, ISiteWithholdings, WithholdingsType } from 'types';
import { centsToDollarsDisplay, toTitleCase } from 'utils';
import EditWithholdingsModal from './EditWithholdingsModal';

const EmployeesTable = styled.table`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  display: flex;
  flex-flow: row nowrap;
  gap: ${toRem(theme.space.sm)};
`;

const SharedCellStyles = css`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  font-size: ${toRem(theme.font.size.xs)};
  flex: 1;

  &:first-child {
    flex: 2;
  }

  &:not(:first-child):not(:nth-child(2)) {
    justify-content: flex-end;
  }
`;

const TableHeaderCell = styled.th`
  ${SharedCellStyles}
  font-weight: ${theme.font.weight.semiBold};
  color: ${theme.color.neutral[500]};

  &:first-child {
    white-space: nowrap;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  display: flex;
  flex-flow: row nowrap;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  ${SharedCellStyles}
  color: ${theme.color.neutral[700]};
  padding: ${toRem(theme.space.sm)} 0;

  & > button:first-child {
    margin-left: 0;
  }

  & > .reset-placeholder {
    visibility: hidden;
  }
`;

interface IEmployeeWithholdingsProps {
  withholdings: ISiteWithholdings;
  isLoading: boolean;
  readOnly: boolean;
}

interface ISelectedEmployeeWithholdings {
  employee: IEmployee;
  withholdings: IEmployeeSiteWithholdings | ISiteWithholdings;
}

const EmployeeWithholdings = ({ withholdings: siteWithholdings, readOnly }: IEmployeeWithholdingsProps) => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const { data: siteEmployees } = useSelector(selectEmployees()) || { data: [], total: null };
  const isLoadingEmployees = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employees.GET_EMPLOYEES_REQUEST));
  const isUpdatingEmployeeWithholdings = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_SITE_ROLE_REQUEST));

  const [selectedEmployeeWithholdings, setSelectedEmployeeWithholdings] = useState<ISelectedEmployeeWithholdings | null>(null);

  const siteHasCustomWithholdings = siteEmployees?.some((employee) => employee.sites?.find((site) => site.id === currentSite?.id)?.withholdings?.type === WithholdingsType.CUSTOM);

  const onEditSave = (updatedWithholdings: IEmployeeSiteWithholdings | ISiteWithholdings) => {
    dispatch(
      updateEmployeeSiteRoleRequest(
        selectedEmployeeWithholdings?.employee.id,
        currentSite?.id,
        { withholdings: { ...updatedWithholdings, type: WithholdingsType.CUSTOM } as IEmployeeSiteWithholdings },
        {
          onSuccess: () => {
            hideEditModal();
            setSelectedEmployeeWithholdings(null);
          },
        }
      )
    );
  };

  const [openEditModal, hideEditModal] = useModal(
    () => (
      <EditWithholdingsModal
        hideModal={() => {
          hideEditModal();
          setSelectedEmployeeWithholdings(null);
        }}
        withholdings={selectedEmployeeWithholdings?.withholdings}
        onSave={onEditSave}
        isProcessing={isUpdatingEmployeeWithholdings}
        modalTitle={`${selectedEmployeeWithholdings?.employee.name}`}
        modalSubtitle={'Enter custom withholding settings. These will override the site defaults for the employee.'}
      />
    ),
    [selectedEmployeeWithholdings, isUpdatingEmployeeWithholdings]
  );

  const onResetConfirm = () => {
    dispatch(
      updateEmployeeSiteRoleRequest(
        selectedEmployeeWithholdings?.employee.id,
        currentSite?.id,
        { withholdings: { ...siteWithholdings, type: WithholdingsType.DEFAULT } },
        {
          successMessage: `Withholdings for ${selectedEmployeeWithholdings?.employee.name} have been reset to site defaults`,
          onSuccess: () => {
            hideResetModal();
            setSelectedEmployeeWithholdings(null);
          },
        }
      )
    );
  };

  const [openResetModal, hideResetModal] = useModal(() => {
    const onClose = () => {
      hideResetModal();
      setSelectedEmployeeWithholdings(null);
    };

    return (
      <ModalFactory customModal onClose={onClose} isActive closeOnOutSideClick={false}>
        <AlertContent
          title="Remove Custom Withholding rules?"
          message={`This will delete the custom withholding rules for ${selectedEmployeeWithholdings?.employee.name}`}
          leftActionLabel="Cancel"
          rightActionLabel="Remove"
          rightActionAppearance="destructive"
          onClose={onClose}
          onLeftAction={onClose}
          onRightAction={onResetConfirm}
          isLoading={isUpdatingEmployeeWithholdings}
          maxWidth={420}
        />
      </ModalFactory>
    );
  }, [selectedEmployeeWithholdings, isUpdatingEmployeeWithholdings]);

  return (
    <>
      <TipCalculationsDescription size="xsmall">Override site settings by creating custom withholding rules for specific employees.</TipCalculationsDescription>
      <ItemContainer data-cy="employee-withholdings-container">
        {isLoadingEmployees ? (
          <LoadingSkeleton />
        ) : (
          <>
            {siteEmployees?.length > 0 ? (
              <EmployeesTable data-cy="employee-withholdings-table">
                <TableHeader data-cy="employee-withholdings-table-header">
                  <TableRow>
                    <TableHeaderCell>Employee</TableHeaderCell>
                    <TableHeaderCell>Type</TableHeaderCell>
                    <TableHeaderCell>Minimum</TableHeaderCell>
                    <TableHeaderCell>Rate</TableHeaderCell>
                    <TableHeaderCell>Cap</TableHeaderCell>
                    {!readOnly && <TableHeaderCell></TableHeaderCell>}
                  </TableRow>
                </TableHeader>
                <TableBody data-cy="employee-withholdings-table-body">
                  {siteEmployees.map((employee) => {
                    const employeeWithholdings = employee.sites?.find((site) => site.id === currentSite?.id)?.withholdings;
                    const isCustom = employeeWithholdings?.type === WithholdingsType.CUSTOM;
                    const withholdingsDisplay = isCustom ? employeeWithholdings : siteWithholdings;

                    return (
                      <TableRow key={`${employee.id}-withholdings`}>
                        <TableCell>
                          <Text size="xsmall" fontWeight="medium" allowWordWrap>
                            {employee.name}
                          </Text>
                        </TableCell>
                        <TableCell>
                          <Tag label={toTitleCase(isCustom && employeeWithholdings ? employeeWithholdings.type : WithholdingsType.DEFAULT)} appearance={Appearance.NEUTRAL} size={Size.SMALL} />
                        </TableCell>
                        <TableCell>
                          <Text size="xsmall">{centsToDollarsDisplay(withholdingsDisplay?.minimum ?? 0)}</Text>
                        </TableCell>
                        <TableCell>
                          <Text size="xsmall">{withholdingsDisplay?.rate ?? 0}%</Text>
                        </TableCell>
                        <TableCell>
                          <Text size="xsmall">{withholdingsDisplay.cap ? centsToDollarsDisplay(withholdingsDisplay.cap) : '--'}</Text>
                        </TableCell>
                        {!readOnly && (
                          <TableCell>
                            <StyledIconButton
                              label="Edit"
                              onClick={() => {
                                setSelectedEmployeeWithholdings({ employee, withholdings: withholdingsDisplay });
                                openEditModal();
                              }}
                              appearance="white"
                              size="small"
                              icon="Edit"
                              data-cy="edit-employee"
                            />
                            {siteHasCustomWithholdings && (
                              <StyledIconButton
                                className={isCustom ? '' : 'reset-placeholder'}
                                label="Reset"
                                onClick={() => {
                                  setSelectedEmployeeWithholdings({ employee, withholdings: withholdingsDisplay });
                                  openResetModal();
                                }}
                                appearance="white"
                                size="small"
                                icon="Retry"
                                disabled={!isCustom}
                                data-cy="reset-employee"
                              />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </EmployeesTable>
            ) : (
              <Text size="xxsmall" fontWeight="medium">
                No employees found
              </Text>
            )}
          </>
        )}
      </ItemContainer>
    </>
  );
};

export default EmployeeWithholdings;
